import { useCallback, useSyncExternalStore } from "react";

// One timer serves every mounted auction surface, including standalone cards.
const subscriptions = new Map<() => void, readonly number[]>();
let timer: ReturnType<typeof setTimeout> | undefined;
let revision = 0;
const MAX_DELAY = 2_147_483_647;

function schedule() {
  clearTimeout(timer);
  timer = undefined;
  const now = Date.now();
  let next = Number.POSITIVE_INFINITY;
  for (const boundaries of subscriptions.values()) {
    for (const boundary of boundaries) {
      if (boundary > now) {
        next = Math.min(next, boundary);
      }
    }
  }
  if (Number.isFinite(next)) {
    timer = setTimeout(notify, Math.min(next - now + 1, MAX_DELAY));
  }
}
function notify() {
  revision += 1;
  for (const listener of subscriptions.keys()) {
    listener();
  }
  schedule();
}
function onVisibilityChange() {
  if (!document.hidden) {
    notify();
  }
}
const getSnapshot = () => revision;
const getServerSnapshot = () => 0;

/** Re-render at known boundaries without polling or changing server status. */
export function useAuctionBoundary(
  startAt: string,
  endAt: string,
  items: readonly {
    startAt?: string | null | undefined;
    endAt?: string | null | undefined;
  }[] = []
) {
  const boundaryKey = [
    startAt,
    endAt,
    ...items.flatMap((item) => [item.startAt ?? startAt, item.endAt ?? endAt]),
  ].join(",");
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!subscriptions.size) {
        document.addEventListener("visibilitychange", onVisibilityChange);
      }
      subscriptions.set(listener, boundaryKey.split(",").map(Date.parse));
      schedule();
      return () => {
        subscriptions.delete(listener);
        schedule();
        if (!subscriptions.size) {
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
      };
    },
    [boundaryKey]
  );
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
